import React, { useRef, useCallback } from "react";
import classnames from "classnames";
import Tippy from "@tippyjs/react";

import useDropdown from "../../hooks/useDropdown";
import { Button } from "../Button";
import { ArrowDown, ArrowUp } from "../Icons";

import styles from "./DropDown.module.scss";

export interface DropDownOption {
  value: string | number;
  label: string;
}

interface Props {
  id: string;
  selected: DropDownOption;
  options: DropDownOption[];
  onChange: (option: DropDownOption) => void;
  disabled?: boolean;
}

const DropDown = ({ id, selected, options, onChange, disabled }: Props) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const optionsWrapperRef = useRef<HTMLUListElement>(null);

  const onSelect = useCallback(
    (e: React.SyntheticEvent<Element, Event>, newValue: React.ReactText) => {
      const newSelected = options.find((option) => option.value === newValue);

      if (newSelected) {
        onChange(newSelected);
      }
    },
    [onChange, options]
  );

  const {
    selectedIdx,
    isDropdownOpened,
    changeDropdownState,
    onKeyDown,
  } = useDropdown(
    buttonRef,
    optionsWrapperRef,
    options,
    onSelect,
    selected.value
  );

  const onOptionClick = (clicked: DropDownOption) => {
    onChange(clicked);
    changeDropdownState(false);
  };

  const content = (
    <ul ref={optionsWrapperRef} className={styles.optionsContainer}>
      {options.map((item, index) => (
        <button
          type="button"
          tabIndex={index}
          className={classnames(styles.optionsItem, {
            [styles.selected]: index === selectedIdx,
          })}
          key={item.value}
          onClick={() => onOptionClick(item)}
        >
          {item.label}
        </button>
      ))}
    </ul>
  );

  return (
    <Tippy
      interactive
      content={content}
      placement="bottom-start"
      arrow={false}
      visible={isDropdownOpened}
      className={styles.switcherTooltip}
    >
      <Button
        id={id}
        disabled={disabled}
        ref={buttonRef}
        variant="inline"
        onKeyDown={onKeyDown}
        onClick={() => changeDropdownState(!isDropdownOpened)}
      >
        {selected.label}
        {isDropdownOpened ? (
          <ArrowUp width="18" height="18" fillColor="#63696F" />
        ) : (
          <ArrowDown width="18" height="18" fillColor="#63696F" />
        )}
      </Button>
    </Tippy>
  );
};

export default DropDown;
